import type { EditableCallback, IsRowFilterable } from "ag-grid-community"

import type { IVariantSummary } from "~/hooks/useVariantTableData"
import { createDateColumn, createUniqueStringColumn } from "~/utils/columnTemplates"
import { deriveLifecycleStage, formatLifecycleStatus } from "~/utils/lifecycleUtils"
import { extractVariantSummaries, ProductTableColumn } from "~/utils/productTableUtils"

import { aggregators } from "../utils/aggregators"
import type { MetadataEditor } from "./supply"
import type { ColumnSpec } from "./types"

export interface LifecycleColumnCtx {
  isEditable: EditableCallback<IVariantSummary>
  metadataEditor: MetadataEditor
  /** `false` when filters apply to leaf rows, which is the only case that can filter groups. */
  groupAggFiltering: boolean | IsRowFilterable | undefined
}

type LifecycleSpec = ColumnSpec<IVariantSummary, LifecycleColumnCtx>

export const LIFECYCLE_STATUS_COLUMN: LifecycleSpec = {
  colId: ProductTableColumn.LifecycleStatus,
  colDef: () =>
    createUniqueStringColumn<IVariantSummary>({
      colId: ProductTableColumn.LifecycleStatus,
      // A group takes the most common stage among its leaves, so this reads the row tree.
      valueGetter: (params) => {
        const lifecycleStatus = aggregators.mode(
          extractVariantSummaries(params).map((entry) => deriveLifecycleStage(entry.settings)),
        )
        return formatLifecycleStatus(lifecycleStatus)
      },
    }),
}

export const ACTIVATED_FROM_COLUMN: LifecycleSpec = {
  colId: ProductTableColumn.ActivatedFrom,
  calculate: (row) => row.settings.activatedFrom ?? "",
  colDef: () => createDateColumn<IVariantSummary>({ colId: ProductTableColumn.ActivatedFrom }),
}

export const ACTIVATED_UNTIL_COLUMN: LifecycleSpec = {
  colId: ProductTableColumn.ActivatedUntil,
  calculate: (row) => row.settings.activatedUntil,
  colDef: (ctx) =>
    createDateColumn<IVariantSummary>({
      colId: ProductTableColumn.ActivatedUntil,
      headerTooltip:
        "Date until which the NOOS product remains active. Fashion products are managed through collections.",
      ...ctx.metadataEditor("activatedUntil", (params) => {
        // Only editable for NOOS products
        const effectiveProductType = params.data?.settings.productType ?? params.data?.productType
        return ctx.isEditable(params) && effectiveProductType === "noos"
      }),
    }),
}

export const PHASING_OUT_FROM_COLUMN: LifecycleSpec = {
  colId: ProductTableColumn.PhasingOutFrom,
  calculate: (row) => row.settings.phasingOutFrom ?? "",
  colDef: (ctx) =>
    createDateColumn<IVariantSummary>({
      colId: ProductTableColumn.PhasingOutFrom,
      filter: ctx.groupAggFiltering === false ? true : undefined,
    }),
}

export const LIFECYCLE_COLUMN_SPECS: readonly LifecycleSpec[] = [
  LIFECYCLE_STATUS_COLUMN,
  ACTIVATED_FROM_COLUMN,
  ACTIVATED_UNTIL_COLUMN,
  PHASING_OUT_FROM_COLUMN,
]
